import React, { useEffect, useMemo, useState } from 'react';
import { useTitle, useValidator } from '@arema/components/Hooks';
import { addCommas, bindClick, cropString, fromUnixLocal, getNowLocal } from '@arema/components/Util';
import { Header, Table, Placeholder, Icon, Button, Modal, Input } from 'react-frontier2';
import { useTranslation } from 'react-i18next';
import { firstBy } from 'thenby';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import API from '../../API';

interface SalesLogData{
	ticket_cost: number,
	commission: number,
	payment_cost: number,
	delivery_cost: number,
	tickets: number,
}

interface StatsData{
	events: (SalesLogData & {
		event_id: number,
		event_name: string,
		commission_return: number,
	})[],
	dates: (SalesLogData & {
		date: number,
		commission_return: number,
	})[]
}

interface StatsForm{
	date_start: DateTime,
	date_end: DateTime,
}

enum Modals{
	CHANGE_DATE = 1,
}

var StatsReport = ()=>{
	var { t } = useTranslation();
	var { setTitle } = useTitle();
	var [query, setQuery] = useSearchParams();
	var [shownModal, setShownModal] = useState<Modals>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [stats, setStats] = useState<StatsData>(null);
	var location = useLocation();
	var data = useValidator<StatsForm>({
		date_start: query.get('ds') ? DateTime.fromFormat(query.get('ds'), 'yyyyy-MM-dd').startOf('day') : getNowLocal().startOf('day').minus({ day: 7 }),
		date_end: query.get('ds') ? DateTime.fromFormat(query.get('de'), 'yyyyy-MM-dd').endOf('day') : getNowLocal().endOf('day'),
	}, {
		date_start: [{ rule: 'empty' }],
		date_end: [{ rule: 'empty' }],
	});

	var getStats = (data: StatsForm)=>{
		API.getSystemStats(data.date_start.toSeconds(), data.date_end.toSeconds()).then(res=>{
			if(res.error) return setLoadError(res.message);
			setStats(res.data);
		}).catch(err=>{
			return setLoadError(t('common.unexpected_code', { code: 'LCL-SYSSTTRP-1' }));
		})
	}

	useEffect(()=>{
		setTitle('Estadisticas');
		// getStats(data.data);
	}, []);

	useEffect(()=>{
		var default_start = getNowLocal().startOf('day').minus({ day: 7 });
		var default_end = getNowLocal().startOf('day');

		var ds = query.get('ds') ? DateTime.fromFormat(query.get('ds'), 'yyyyy-MM-dd').startOf('day') : default_start;
		var de = query.get('de') ? DateTime.fromFormat(query.get('de'), 'yyyyy-MM-dd').startOf('day') : default_end;

		var data_ds = data.data.date_start || null;
		var data_de = data.data.date_end || null;
		
		if(ds!==data_ds || de!==data_de){
			setStats(null);
			getStats({
				date_start: ds,
				date_end: de
			});
		}
	}, [location.search]);

	var changeDate = (date: number)=>{
		return ()=>{
			var nd = fromUnixLocal(date);
			var new_data = {
				date_start: nd.startOf('day'),
				date_end: nd.endOf('day'),
			};
			data.setData(new_data);
			setStats(null);
			setQuery({
				ds: nd.toFormat('yyyy-MM-dd'),
				de: nd.toFormat('yyyy-MM-dd'),
			});
			getStats(new_data);
		}
	}

	var searchDates = ()=>{
		setStats(null);
		setQuery({
			ds: data.data.date_start.toFormat('yyyy-MM-dd'),
			de: data.data.date_end.toFormat('yyyy-MM-dd'),
		});
		setShownModal(null);
		getStats(data.data);
	}

	var onDateChange = (key: keyof StatsForm)=>{
		return (val: Date)=>{
			var dt = DateTime.fromJSDate(val);
			if(key==='date_start'){
				dt = dt.startOf('day');
			}else{
				dt = dt.endOf('day');
			}
			data.setValue(key, dt);
		}
	}

	var DateRange = useMemo(()=>{
		var same_year = data.data.date_start && data.data.date_end && data.data.date_start.hasSame(data.data.date_end, 'year');
		return <>
			{data.data.date_start.toFormat(`dd/MMM${same_year ? '' : '/yyyy'}`)}
			<Icon name='arrow-right' style={{ margin: '0 5px' }} />
			{data.data.date_end.toFormat(`dd/MMM${same_year ? '' : '/yyyy'}`)}
		</>
	}, [data.data]);

	var dates_totals = useMemo(()=>{
		var totals = {
			ticket_cost: 0,
			commission: 0,
			commission_return: 0
		}
		if(!stats) return totals;
		for(var i of stats.dates){
			totals.ticket_cost += i.ticket_cost;
			totals.commission += i.commission+i.payment_cost+i.delivery_cost;
			totals.commission_return += i.commission_return;
		}
		return totals;
	}, [stats]);

	if(loadError) return <Header text={t('common.error')} subtext={loadError} />
	return <div className="ar stats-report-screen">
		<Header text={'Estadisticas'} subtext={DateRange} />
		<Button text='Cambiar fechas' style={{ display: 'block', margin: 'auto', marginTop: 5, marginBottom: 15, width: 200 }} onClick={bindClick(setShownModal, Modals.CHANGE_DATE)} />
		<div className="contents">
			<Table striped>
				<Table.Head
					title='Eventos'
					collapsingIndexes={[0, 2, 3, 4]}
					data={[['ID', 'Evento', 'Venta', 'Retorno', 'Comisión']]}
				/>
				<Table.Body>
					{!stats ? (new Array(10).fill('0').map((a, i)=>(
						<Table.Row key={`EVRWPL-${i}`} data={[
							<Placeholder width={50} random={false} />,
							<Placeholder width={100} />,
							<Placeholder width={30} />,
							<Placeholder width={30} />,
							<Placeholder width={30} />,
						]} />
					))) : stats.events.sort(firstBy('ticket_cost', 'desc').thenBy('commission', 'desc')).map(a=>(
						<Table.Row selectable
							compact
							key={`EVRW-${a.event_id}`}
							as={Link}
							to={`/events/${a.event_id}/balance`}
							className={'compact'}
							data={[
								a.event_id,
								cropString(a.event_name, 64, true),
								addCommas(a.ticket_cost),
								(a.commission_return===null || a.commission_return===0) ? (
									<Icon name='minus' color='gray' />
								) : (
									addCommas(a.ticket_cost*a.commission_return)
								),
								addCommas(a.commission),
							]}
						/>
					))}
				</Table.Body>
			</Table>
			<Table style={{ maxWidth: 850 }}>
				<Table.Head
					title='Fechas'
					collapsingIndexes={[1, 2, 3]}
					data={[['Fecha', 'Venta', 'Retorno', 'Comisión']]}
				/>
				<Table.Body>
					{!stats ? (new Array(10).fill('0').map((a, i)=>(
						<Table.Row key={`DTRWPL-${i}`} data={[
							<Placeholder width={80} random={false} />,
							<Placeholder width={30} />,
							<Placeholder width={30} />,
							<Placeholder width={30} />,
						]} />
					))) : <>
						{!!dates_totals && <>
							<Table.Row>
								<Table.Cell>{DateRange}</Table.Cell>
								<Table.Cell centered>{addCommas(dates_totals.ticket_cost)}</Table.Cell>
								<Table.Cell centered>{addCommas(dates_totals.commission_return)}</Table.Cell>
								<Table.Cell centered>{addCommas(dates_totals.commission)}</Table.Cell>
							</Table.Row>
							<Table.Divider />
						</>}
						{stats.dates.sort(firstBy('date', 'asc')).map(a=>(
							<Table.Row selectable
								key={`DTRW-${a.date}`}
								onClick={changeDate(a.date)}
								data={[
									<b>{fromUnixLocal(a.date).toFormat('dd/MMM/yyyy')}</b>,
									addCommas(a.ticket_cost),
									a.commission_return===null ? (
										<Icon name='minus' color='gray' />
									) : (
										addCommas(a.commission_return)
									),
									addCommas(a.commission+a.delivery_cost+a.payment_cost),
								]}
							/>
						))}
					</>}
				</Table.Body>
			</Table>
		</div>
		<Modal open={shownModal===Modals.CHANGE_DATE} onClose={bindClick(setShownModal)} size={'mini'}>
			<Modal.Header>lmao</Modal.Header>
			<Modal.Content>
				<Input label={t('common.date_start')} calendar={{ 
					mode: 'date', 
					format: 'dd/MMM/yyyy', 
					date: data.data.date_start.toJSDate(),
					onSelected: onDateChange('date_start'),
				}} />
				<Input label={t('common.date_end')} calendar={{ 
					mode: 'date',
					format: 'dd/MMM/yyyy',
					date: data.data.date_end.toJSDate(),
					onSelected: onDateChange('date_end'),
				}} />
			</Modal.Content>
			<Modal.Actions>
				<Button text={t('common.close')} basic onClick={bindClick(setShownModal)} />
				<Button text={t('common.save')} color={'black'} onClick={searchDates} />
			</Modal.Actions>
		</Modal>
	</div>
}

export default StatsReport;